import { motion } from 'framer-motion';
import { Calendar, MapPin, User, Sparkles, ArrowRightLeft, Gift, Target, Clock, Home } from 'lucide-react';
import { Badge } from '../ui/Badge';

/**
 * BusinessDetails - Premium about section with warm, inviting design
 */
export function BusinessDetails({
    description,
    categories = [],
    zones = [],
    eventTime,
    linkifyText,
    founderName,
    founderAbout,
    founderImageUrl,
    type,
    activeDays,
    address
}) {
    const formatEventTime = (raw) => {
        const date = new Date(raw);
        return date.toLocaleString('he-IL', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    // Determine the title based on the type
    const getDescriptionTitle = () => {
        if (type === 'event') {
            return 'אודות המפגש';
        } else if (type === 'barter') {
            return 'אודות הברטר';
        } else {
            return 'אודות העסק';
        }
    };

    // Parse barter description to extract what's offered and what's requested
    const parseBarterDescription = (desc) => {
        if (!desc || type !== 'barter') return null;

        // Keywords that indicate "offered" section
        const offeredKeywords = /(?:^|\n)\s*(?:מציע[הת]?|מה אני מציע[הת]?)\s*:?\s*/i;
        // Keywords that indicate "requested" section
        const requestedKeywords = /(?:^|\n)\s*(?:מחפש[תים]?|דורש[תים]?|רוצ[הים]?|מעוניין[תים]?|מה אני מחפש[תים]?|מה אני דורש[תים]?)\s*:?\s*/i;

        const offeredMatch = desc.match(offeredKeywords);
        const requestedMatch = desc.match(requestedKeywords);

        if (offeredMatch && requestedMatch) {
            // Find the positions of both keywords
            const offeredStart = offeredMatch.index + offeredMatch[0].length;
            const requestedStart = requestedMatch.index + requestedMatch[0].length;

            let offered = '';
            let requested = '';

            // Determine which comes first
            if (offeredStart < requestedStart) {
                // "Offered" comes first
                offered = desc.substring(offeredStart, requestedMatch.index).trim();
                requested = desc.substring(requestedStart).trim();
            } else {
                // "Requested" comes first
                requested = desc.substring(requestedStart, offeredMatch.index).trim();
                offered = desc.substring(offeredStart).trim();
            }

            // Clean up any trailing patterns from the extracted text
            offered = offered.replace(/(?:\n\s*(?:מחפש[תים]?|דורש[תים]?|רוצ[הים]?|מעוניין[תים]?|מה אני מחפש[תים]?)\s*:?\s*).*$/is, '').trim();
            requested = requested.replace(/(?:\n\s*(?:מציע[הת]?|מה אני מציע[הת]?)\s*:?\s*).*$/is, '').trim();

            if (offered && requested) {
                return {
                    offered,
                    requested,
                    hasStructuredData: true
                };
            }
        }

        // If no pattern matches, return the full description
        return { fullDescription: desc, hasStructuredData: false };
    };

    const barterData = type === 'barter' ? parseBarterDescription(description) : null;

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="px-6 py-8 bg-gradient-to-b from-white to-surface-muted/30 space-y-8"
        >
            {/* Categories with refined badges */}
            {categories && categories.length > 0 && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 }}
                    className="flex flex-wrap gap-2"
                >
                    {categories.slice(0, 4).map((cat, idx) => (
                        <motion.div
                            key={idx}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.25 + idx * 0.05 }}
                        >
                            <Badge
                                variant="brand"
                                size="md"
                                className="font-semibold shadow-sm hover:shadow-md transition-shadow"
                            >
                                {cat}
                            </Badge>
                        </motion.div>
                    ))}
                </motion.div>
            )}

            {/* About / Description - Warm and inviting */}
            {description && type === 'barter' && barterData?.hasStructuredData ? (
                // Barter-specific structured display
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="space-y-4"
                >
                    {/* Barter Header */}
                    <div className="flex items-center gap-2 mb-2">
                        <ArrowRightLeft className="w-5 h-5 text-brand-500" strokeWidth={2.5} />
                        <h2 className="text-lg font-bold text-ink-strong tracking-tight">
                            פרטי הברטר
                        </h2>
                    </div>

                    {/* What's Offered */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.35, type: "spring", stiffness: 100 }}
                        className="bg-gradient-to-br from-success-50 to-success-50 border-2 border-success-100/60 rounded-2xl p-6 shadow-md hover:shadow-lg transition-all"
                    >
                        <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-success-500 to-success-500 flex items-center justify-center shadow-lg shadow-success-500/30">
                                <Gift className="w-6 h-6 text-white" strokeWidth={2.5} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-bold uppercase tracking-wider text-success-700 mb-3">
                                    מה מוצע
                                </p>
                                <p className="text-base leading-relaxed text-ink-strong font-medium whitespace-pre-wrap break-words">
                                    {barterData.offered.split(/\r?\n/).map((line, idx, arr) => (
                                        <span key={idx}>
                                            {linkifyText ? linkifyText(line) : line}
                                            {idx < arr.length - 1 && <br />}
                                        </span>
                                    ))}
                                </p>
                            </div>
                        </div>
                    </motion.div>

                    {/* What's Requested */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4, type: "spring", stiffness: 100 }}
                        className="bg-gradient-to-br from-info-50 to-brand-50 border-2 border-info-100/60 rounded-2xl p-6 shadow-md hover:shadow-lg transition-all"
                    >
                        <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-info-500 to-brand-500 flex items-center justify-center shadow-lg shadow-info-500/30">
                                <Target className="w-6 h-6 text-white" strokeWidth={2.5} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-bold uppercase tracking-wider text-info-700 mb-3">
                                    מה מבוקש
                                </p>
                                <p className="text-base leading-relaxed text-ink-strong font-medium whitespace-pre-wrap break-words">
                                    {barterData.requested.split(/\r?\n/).map((line, idx, arr) => (
                                        <span key={idx}>
                                            {linkifyText ? linkifyText(line) : line}
                                            {idx < arr.length - 1 && <br />}
                                        </span>
                                    ))}
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            ) : description ? (
                // Regular description display for non-barter or unstructured barter
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="space-y-3"
                >
                    <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-warning-500" strokeWidth={2} />
                        <h2 className="text-sm font-bold text-ink-strong tracking-tight">
                            {getDescriptionTitle()}
                        </h2>
                    </div>
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-line-subtle">
                        <p className="text-base leading-relaxed text-ink whitespace-pre-wrap break-words">
                            {description.split(/\r?\n/).map((line, idx, arr) => (
                                <span key={idx}>
                                    {linkifyText ? linkifyText(line) : line}
                                    {idx < arr.length - 1 && <br />}
                                </span>
                            ))}
                        </p>
                    </div>
                </motion.div>
            ) : null}

            {/* Event Time - Refined card */}
            {eventTime && (
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.35, type: "spring", stiffness: 100 }}
                    className="bg-gradient-to-br from-warning-50 to-warning-50 border-2 border-warning-100/60 rounded-2xl p-5 shadow-md hover:shadow-lg transition-all"
                >
                    <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-warning-500 to-warning-500 flex items-center justify-center shadow-lg shadow-warning-500/20">
                            <Calendar className="w-6 h-6 text-white" strokeWidth={2.5} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-bold uppercase tracking-wider text-warning-700 mb-1.5">
                                תאריך מפגש
                            </p>
                            <p className="text-base text-ink-strong font-semibold">
                                {formatEventTime(eventTime)}
                            </p>
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Address - Refined card */}
            {address && (
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.38, type: "spring", stiffness: 100 }}
                    className="bg-gradient-to-br from-brand-50 to-secondary-50 border-2 border-brand-200/60 rounded-2xl p-5 shadow-md hover:shadow-lg transition-all"
                >
                    <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-brand-400 to-secondary-500 flex items-center justify-center shadow-lg shadow-brand-500/20">
                            <Home className="w-6 h-6 text-white" strokeWidth={2.5} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-bold uppercase tracking-wider text-brand-700 mb-1.5">
                                כתובת
                            </p>
                            <p className="text-base text-ink-strong font-semibold">
                                {address}
                            </p>
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Zones - Refined card with location badges */}
            {zones && zones.length > 0 && (
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4, type: "spring", stiffness: 100 }}
                    className="bg-gradient-to-br from-success-50 to-success-50 border-2 border-success-100/60 rounded-2xl p-5 shadow-md hover:shadow-lg transition-all"
                >
                    <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-success-500 to-success-500 flex items-center justify-center shadow-lg shadow-success-500/20">
                            <MapPin className="w-6 h-6 text-white" strokeWidth={2.5} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-bold uppercase tracking-wider text-success-700 mb-3">
                                אזורי פעילות
                            </p>
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.45 }}
                                className="flex flex-wrap gap-2"
                            >
                                {zones.map((zone, idx) => {
                                    const zoneDisplay = typeof zone === 'string' ? zone : (zone.zone_display_name || zone.zone_id);
                                    return (
                                        <motion.div
                                            key={idx}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.5 + idx * 0.05 }}
                                        >
                                            <Badge
                                                variant="secondary"
                                                size="md"
                                                className="font-semibold shadow-sm hover:shadow-md transition-shadow"
                                            >
                                                {zoneDisplay}
                                            </Badge>
                                        </motion.div>
                                    );
                                })}
                            </motion.div>
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Working Days - Only for businesses with active_days data */}
            {type === 'business' && activeDays && activeDays.length > 0 && (
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.45, type: "spring", stiffness: 100 }}
                    className="bg-gradient-to-br from-info-50 to-brand-50 border-2 border-info-100/60 rounded-2xl p-5 shadow-md hover:shadow-lg transition-all"
                >
                    <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-info-500 to-brand-500 flex items-center justify-center shadow-lg shadow-info-500/20">
                            <Clock className="w-6 h-6 text-white" strokeWidth={2.5} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-bold uppercase tracking-wider text-info-700 mb-3">
                                ימי פעילות
                            </p>
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.5 }}
                                className="flex flex-wrap gap-2"
                            >
                                {[
                                    { day: 0, label: 'ראשון' },
                                    { day: 1, label: 'שני' },
                                    { day: 2, label: 'שלישי' },
                                    { day: 3, label: 'רביעי' },
                                    { day: 4, label: 'חמישי' },
                                    { day: 5, label: 'שישי' },
                                    { day: 6, label: 'שבת' }
                                ].map((dayInfo, idx) => {
                                    const isActive = activeDays.includes(dayInfo.day);
                                    return (
                                        <motion.div
                                            key={dayInfo.day}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.55 + idx * 0.05 }}
                                        >
                                            <div
                                                className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${isActive
                                                        ? 'bg-info-500 text-white shadow-md'
                                                        : 'bg-surface-sunken text-ink-subtle'
                                                    }`}
                                            >
                                                {dayInfo.label}
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </motion.div>
                            {activeDays.length === 7 && (
                                <p className="text-xs text-info-600 font-medium mt-3">
                                    פעיל כל השבוע
                                </p>
                            )}
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Founder Profile Section - Premium design */}
            {(founderName || founderAbout || founderImageUrl) && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.45, type: "spring", stiffness: 80 }}
                    className="relative bg-gradient-to-br from-warning-50 via-warning-50 to-warning-50 border-2 border-warning-100/50 rounded-3xl p-8 shadow-lg overflow-hidden"
                >
                    {/* Decorative background elements */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-warning-100/30 to-transparent rounded-full blur-3xl" />
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-warning-100/30 to-transparent rounded-full blur-2xl" />

                    <div className="relative">
                        <div className="flex items-center gap-2 mb-6">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-warning-500 to-warning-500 flex items-center justify-center shadow-md">
                                <User className="w-5 h-5 text-white" strokeWidth={2.5} />
                            </div>
                            <h3 className="text-sm font-bold uppercase tracking-wider text-warning-700">
                                המייסד/ת
                            </h3>
                        </div>

                        <div className="flex flex-col items-center text-center space-y-5">
                            {/* Founder Image */}
                            {founderImageUrl && (
                                <motion.div
                                    initial={{ scale: 0.8, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ delay: 0.5, duration: 0.5, type: "spring" }}
                                    className="relative"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-br from-warning-500 to-warning-500 rounded-full blur-xl opacity-30" />
                                    <img
                                        src={founderImageUrl}
                                        alt={founderName || 'מייסד/ת'}
                                        className="relative w-28 h-28 rounded-full object-cover shadow-2xl border-4 border-white ring-4 ring-warning-100/50"
                                    />
                                </motion.div>
                            )}

                            {/* Founder Name */}
                            {founderName && (
                                <motion.h4
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.55 }}
                                    className="text-2xl font-bold text-ink-strong tracking-tight"
                                >
                                    {founderName}
                                </motion.h4>
                            )}

                            {/* Founder About */}
                            {founderAbout && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.6 }}
                                    className="bg-white/80 backdrop-blur-sm rounded-2xl p-5 shadow-md border border-warning-100"
                                >
                                    <p className="text-sm leading-relaxed text-ink max-w-md whitespace-pre-wrap break-words">
                                        {founderAbout.split(/\r?\n/).map((line, idx, arr) => (
                                            <span key={idx}>
                                                {linkifyText ? linkifyText(line) : line}
                                                {idx < arr.length - 1 && <br />}
                                            </span>
                                        ))}
                                    </p>
                                </motion.div>
                            )}
                        </div>
                    </div>
                </motion.div>
            )}
        </motion.div>
    );
}

export default BusinessDetails;
